/**
 * Google Ads CSV Writer v1
 *
 * Generates CSV files compatible with Google Ads Editor bulk upload.
 * Supports Search, Display, and Demand Gen campaign types.
 *
 * @see https://support.google.com/google-ads/editor/answer/57747
 */

import { CampaignData, PlatformWriter, ValidationResult } from '../../types';

/**
 * Mapping from canonical objectives to Google Ads campaign types
 */
const GOOGLE_CAMPAIGN_TYPES: Record<string, string> = {
  awareness: 'Display',
  brand_awareness: 'Display',
  traffic: 'Search',
  engagement: 'Display',
  leads: 'Search',
  sales: 'Search',
  conversions: 'Search',
  video_views: 'Video',
};

/**
 * Google Ads CSV column headers for Search campaigns
 */
const GOOGLE_HEADERS = [
  'Campaign',
  'Campaign Type',
  'Campaign Status',
  'Budget',
  'Budget Type',
  'Bid Strategy Type',
  'Start Date',
  'End Date',
  'Ad Group',
  'Ad Group Status',
  'Ad Group Type',
  'Max CPC',
  'Headline 1',
  'Headline 2',
  'Headline 3',
  'Headline 4',
  'Headline 5',
  'Description 1',
  'Description 2',
  'Final URL',
  'Path 1',
  'Path 2',
  'Image',
];

export class GoogleAdsWriterV1 implements PlatformWriter {
  readonly platform = 'google_ads' as const;
  readonly version = 'v1';
  readonly formatName = 'Google Ads Editor Import';

  validate(data: CampaignData): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Required fields
    if (!data.title) {
      errors.push('Campaign name is required');
    }
    if (!data.ad_copy?.link_url) {
      errors.push('Final URL (landing page) is required');
    }

    // Budget validation
    if (!data.budget?.daily) {
      errors.push('Daily budget is required for Google Ads');
    }

    // Headlines validation for RSA
    const headlines = this.getHeadlines(data);
    if (headlines.length < 3) {
      errors.push(
        `Responsive Search Ads require at least 3 headlines. Found ${headlines.length}.`
      );
    }

    // Descriptions validation for RSA
    const descriptions = this.getDescriptions(data);
    if (descriptions.length < 2) {
      errors.push(
        `Responsive Search Ads require at least 2 descriptions. Found ${descriptions.length}.`
      );
    }

    // Character limit warnings
    headlines.forEach((headline, idx) => {
      if (headline.length > 30) {
        warnings.push(
          `Headline ${idx + 1} is ${headline.length} characters. Max is 30. Will be truncated.`
        );
      }
    });

    descriptions.forEach((desc, idx) => {
      if (desc.length > 90) {
        warnings.push(
          `Description ${idx + 1} is ${desc.length} characters. Max is 90. Will be truncated.`
        );
      }
    });

    // Keywords check for Search campaigns
    const channelType =
      data.platform_specific?.google_ads?.channel_type || 'SEARCH';
    if (
      channelType === 'SEARCH' &&
      !data.platform_specific?.google_ads?.keywords?.length
    ) {
      warnings.push(
        'No keywords provided. You may need to add keywords in Google Ads Editor.'
      );
    }

    // Image check for Display campaigns
    if (channelType === 'DISPLAY' && !data.media?.some((m) => m.type === 'image')) {
      warnings.push(
        'Display campaigns benefit from images. Consider adding image assets.'
      );
    }

    return { valid: errors.length === 0, errors, warnings };
  }

  transform(data: CampaignData): Record<string, string>[] {
    const rows: Record<string, string>[] = [];

    const channelType =
      data.platform_specific?.google_ads?.channel_type ||
      GOOGLE_CAMPAIGN_TYPES[data.objective?.toLowerCase()] ||
      'Search';

    // Format dates (Google Ads uses YYYY-MM-DD)
    const startDate = this.formatDate(data.timeline?.start_date);
    const endDate = data.timeline?.end_date
      ? this.formatDate(data.timeline.end_date)
      : '';

    // Bidding strategy
    const biddingStrategy =
      data.platform_specific?.google_ads?.bidding_strategy || 'Maximize clicks';

    // Check if we have full campaigns structure
    const googleCampaigns = data.campaigns?.filter(c => c.platform === 'google_ads') || [];

    if (googleCampaigns.length > 0 && googleCampaigns.some(c => c.ad_groups && c.ad_groups.length > 0)) {
      // Use full campaigns structure - generate one row per ad variation
      for (const campaign of googleCampaigns) {
        const campaignName = campaign.name || data.title;

        for (const adGroup of campaign.ad_groups || []) {
          for (let adIdx = 0; adIdx < (adGroup.ad_variations?.length || 0); adIdx++) {
            const ad = adGroup.ad_variations[adIdx];

            // Get headlines for this ad
            const adHeadlines = ad.headlines && ad.headlines.length >= 3
              ? ad.headlines
              : this.getHeadlines(data);

            // Get descriptions for this ad
            const adDescriptions = ad.descriptions && ad.descriptions.length >= 2
              ? ad.descriptions
              : this.getDescriptions(data);

            // Get final URL
            const finalUrl = ad.link_url || ad.landing_page || data.ad_copy?.link_url || '';

            // Extract path components from URL
            const { path1, path2 } = this.extractPathFromUrl(finalUrl);

            // Get image for this ad
            let imageUrl = '';
            if (ad.media && ad.media.length > 0) {
              const imageAsset = ad.media.find((m: any) => m.type === 'image');
              imageUrl = imageAsset?.public_url || '';
            } else {
              const imageAsset = data.media?.find((m) => m.type === 'image');
              imageUrl = imageAsset?.public_url || '';
            }

            rows.push({
              Campaign: campaignName,
              'Campaign Type': channelType,
              'Campaign Status': 'Paused',
              Budget: data.budget?.daily?.toString() || '10',
              'Budget Type': 'Daily',
              'Bid Strategy Type': biddingStrategy,
              'Start Date': startDate,
              'End Date': endDate,
              'Ad Group': adGroup.name || `${campaignName} - Ad Group`,
              'Ad Group Status': 'Enabled',
              'Ad Group Type': 'Standard',
              'Max CPC': '1.00',
              'Headline 1': this.truncate(adHeadlines[0] || '', 30),
              'Headline 2': this.truncate(adHeadlines[1] || '', 30),
              'Headline 3': this.truncate(adHeadlines[2] || '', 30),
              'Headline 4': this.truncate(adHeadlines[3] || '', 30),
              'Headline 5': this.truncate(adHeadlines[4] || '', 30),
              'Description 1': this.truncate(adDescriptions[0] || '', 90),
              'Description 2': this.truncate(adDescriptions[1] || '', 90),
              'Final URL': finalUrl,
              'Path 1': path1,
              'Path 2': path2,
              Image: imageUrl,
            });
          }
        }
      }
    }

    // Fallback: If no rows generated from campaigns structure, use legacy single-row approach
    if (rows.length === 0) {
      const headlines = this.getHeadlines(data);
      const descriptions = this.getDescriptions(data);
      const { path1, path2 } = this.extractPathFromUrl(data.ad_copy?.link_url);
      const imageAsset = data.media?.find((m) => m.type === 'image');

      rows.push({
        Campaign: data.title,
        'Campaign Type': channelType,
        'Campaign Status': 'Paused',
        Budget: data.budget?.daily?.toString() || '10',
        'Budget Type': 'Daily',
        'Bid Strategy Type': biddingStrategy,
        'Start Date': startDate,
        'End Date': endDate,
        'Ad Group': `${data.title} - Ad Group`,
        'Ad Group Status': 'Enabled',
        'Ad Group Type': 'Standard',
        'Max CPC': '1.00',
        'Headline 1': this.truncate(headlines[0] || '', 30),
        'Headline 2': this.truncate(headlines[1] || '', 30),
        'Headline 3': this.truncate(headlines[2] || '', 30),
        'Headline 4': this.truncate(headlines[3] || '', 30),
        'Headline 5': this.truncate(headlines[4] || '', 30),
        'Description 1': this.truncate(descriptions[0] || '', 90),
        'Description 2': this.truncate(descriptions[1] || '', 90),
        'Final URL': data.ad_copy?.link_url || '',
        'Path 1': path1,
        'Path 2': path2,
        Image: imageAsset?.public_url || '',
      });
    }

    return rows;
  }

  getHeaders(): string[] {
    return GOOGLE_HEADERS;
  }

  /**
   * Get headlines from campaign data
   * Combines explicit headlines array with headline field
   */
  private getHeadlines(data: CampaignData): string[] {
    const headlines: string[] = [];

    // Add from headlines array if exists
    if (data.ad_copy?.headlines?.length) {
      headlines.push(...data.ad_copy.headlines);
    }

    // Add main headline if not already included
    if (data.ad_copy?.headline && !headlines.includes(data.ad_copy.headline)) {
      headlines.unshift(data.ad_copy.headline);
    }

    // Generate additional headlines from body text if needed
    if (headlines.length < 3 && data.ad_copy?.body_text) {
      // Try to create headlines from body text sentences
      const sentences = data.ad_copy.body_text
        .split(/[.!?]+/)
        .filter((s) => s.trim().length > 0 && s.trim().length <= 30);

      while (headlines.length < 3 && sentences.length > 0) {
        const sentence = sentences.shift()?.trim();
        if (sentence && !headlines.includes(sentence)) {
          headlines.push(sentence);
        }
      }
    }

    // Generate generic headlines if still not enough
    while (headlines.length < 3) {
      headlines.push(data.title.slice(0, 30));
    }

    return headlines.slice(0, 15); // Google allows max 15 headlines
  }

  /**
   * Get descriptions from campaign data
   */
  private getDescriptions(data: CampaignData): string[] {
    const descriptions: string[] = [];

    // Add from descriptions array if exists
    if (data.ad_copy?.descriptions?.length) {
      descriptions.push(...data.ad_copy.descriptions);
    }

    // Add body text as description
    if (data.ad_copy?.body_text && !descriptions.includes(data.ad_copy.body_text)) {
      descriptions.push(data.ad_copy.body_text);
    }

    // Add description field
    if (data.ad_copy?.description && !descriptions.includes(data.ad_copy.description)) {
      descriptions.push(data.ad_copy.description);
    }

    // Generate if not enough
    while (descriptions.length < 2) {
      descriptions.push(`Learn more about ${data.title}`);
    }

    return descriptions.slice(0, 4); // Google allows max 4 descriptions
  }

  /**
   * Extract path components from URL for display URL
   */
  private extractPathFromUrl(url?: string): { path1: string; path2: string } {
    if (!url) return { path1: '', path2: '' };

    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/').filter(Boolean);
      return {
        path1: this.truncate(pathParts[0] || '', 15),
        path2: this.truncate(pathParts[1] || '', 15),
      };
    } catch {
      return { path1: '', path2: '' };
    }
  }

  /**
   * Format date to YYYY-MM-DD for Google Ads
   */
  private formatDate(dateStr?: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0];
    } catch {
      return dateStr;
    }
  }

  /**
   * Truncate string to max length
   */
  private truncate(str: string, maxLength: number): string {
    if (str.length <= maxLength) return str;
    return str.slice(0, maxLength - 3) + '...';
  }
}
